import AppButton from '@nxsys/components/AppButton';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppLoader from '@nxsys/components/AppLoader';
import AppSelect from '@nxsys/components/AppSelect';
import { irelandCountyOptions } from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useUpdateEmployeeById } from '@nxsys/hooks/api';
import { UpdateEmployeeByIdParams } from '@nxsys/types/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { EmployeeRecordType } from '@nxsys/types/models/employee';
import { IRELAND } from '@nxsys/store/constants/keys';
import { countryList } from '@nxsys/utils';
import {
  formatPostCode,
  postCodeValidationRule,
} from '@nxsys/utils/formFieldValidation';
import { showMessage } from '@nxsys/utils/message';
import { Col, Form, Row, Typography } from 'antd';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useDrawerSave } from './useDrawerSave';

type Props = {
  data: EmployeeRecordType | undefined;
  onRefetch?: () => void;
  readOnly?: boolean;
  /** Field name to scroll to + focus once data loads (NX-4682 quick-edit). */
  focusField?: string;
};

const Address = ({ data, onRefetch, readOnly, focusField }: Props) => {
  const { messages } = useTypedIntl();
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const [form] = Form.useForm();
  const { run: updateEmployee, loading: isUpdatingEmployee } =
    useUpdateEmployeeById();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        address1: data.address?.[0] ?? '',
        address2: data.address?.[1] ?? '',
        address3: data.address?.[2] ?? '',
        city: data.city ?? '',
        county: data.county ?? '',
        postCode: data.postCode ?? '',
        country: data.country
          ? (countryList.find((item) => item.value === data.country) ?? null)
          : null,
      });
    }
  }, [data]);

  // Scroll to + focus a field when opened from the dashboard "Action Required"
  // quick-edit drawer (NX-4682). No-op on the standalone detail page.
  useEffect(() => {
    if (!focusField || !data) return;
    const timer = setTimeout(() => {
      // AppInput doesn't forward focus() to its native input, and antd prefixes
      // field ids with the form name — target the control via its id suffix.
      const el = document.querySelector<HTMLElement>(
        `[id="${focusField}"], [id$="_${focusField}"]`,
      );
      if (el) {
        // Smoothly center the whole field, then focus WITHOUT re-scrolling so
        // the browser's focus auto-scroll doesn't clobber the animation.
        const target = el.closest('.ant-form-item') ?? el;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      } else {
        form.scrollToField(focusField, { behavior: 'smooth', block: 'center' });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [focusField, data, form]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!data?.id) return;

      const params: Partial<UpdateEmployeeByIdParams> = {
        address: [
          values.address1 ?? '',
          values.address2 ?? '',
          values.address3 ?? '',
        ],
        city: values.city ?? '',
        county:
          typeof values.county === 'object'
            ? ((values.county as any)?.value ?? '')
            : (values.county ?? ''),
        postCode: values.postCode ?? '',
        country: values.country?.value ?? '',
      };

      await updateEmployee(data.id, params as UpdateEmployeeByIdParams);
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      onRefetch?.();
    } catch (error: any) {
      if (error?.errorFields) return;
      console.error(error);
    }
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header
  // (always visible) instead of at the bottom of the form.
  const inDrawer = useDrawerSave(handleSave, isUpdatingEmployee, !readOnly);

  if (!data)
    return (
      <div className="relative h-[100px]">
        <AppLoader />
      </div>
    );

  return (
    <div>
      <Typography.Title
        level={5}
        className="!mb-8 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.address']}
      </Typography.Title>

      <AppFormContainer
        form={form}
        layout="vertical"
        labelCol={{ span: 24 }}
        wrapperCol={{ span: 24 }}
        disabled={readOnly}
      >
        {/* Address Line 1 */}
        <Form.Item
          label={messages['common.addressLine1']}
          name="address1"
          rules={[
            {
              required: true,
              message: messages['form.pleaseEnter'],
            },
          ]}
        >
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>

        {/* Address Line 2 */}
        <Form.Item
          label={messages['common.addressLine2']}
          name="address2"
          rules={[
            {
              required: true,
              message: messages['form.pleaseEnter'],
            },
          ]}
        >
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>

        {/* Address Line 3 */}
        <Form.Item
          label={messages['common.addressLine3'] ?? 'Address Line 3'}
          name="address3"
        >
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>

        {/* City | County */}
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.cityTown']} name="city">
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.county']} name="county">
              {isIreland ? (
                <AppSelect
                  showSearch
                  options={irelandCountyOptions}
                  placeholder={messages['form.select']}
                />
              ) : (
                <AppInput placeholder={messages['form.enter']} />
              )}
            </Form.Item>
          </Col>
        </Row>

        {/* Post Code | Country */}
        <Row gutter={24}>
          <Col xs={24} sm={12}>
            <Form.Item
              label={
                isIreland
                  ? messages['common.eirCode']
                  : messages['common.postCode']
              }
              name="postCode"
              rules={[
                {
                  required: true,
                  message: messages['form.pleaseEnter'],
                },
                postCodeValidationRule(messages, payrollCountry),
              ]}
            >
              <AppInput
                placeholder={messages['form.enter']}
                upperCaseAll
                onChange={(e) => {
                  form.setFieldValue(
                    'postCode',
                    formatPostCode(e.target.value, payrollCountry),
                  );
                }}
                maxLength={8}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={messages['common.country']} name="country">
              <AppSelect
                options={countryList}
                className="w-full !min-w-0"
                placeholder={messages['form.select']}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Action Buttons */}
        {!readOnly && !inDrawer && (
          <div className="flex items-center justify-start gap-3 pt-4">
            <AppButton
              type="primary"
              name={messages['common.save']}
              leftIcon={
                <Icons.IconCheckCircle
                  color={defaultTheme.theme.palette.white}
                />
              }
              onClick={handleSave}
              loading={isUpdatingEmployee}
            />
          </div>
        )}
      </AppFormContainer>
    </div>
  );
};

export default Address;
